import { HelpCircle } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { placeHelpTip, samePlacement, type TipPlacement } from './helpTipPlacement'

/** A small "?" that opens the explanation of the control beside it; Escape or a click outside closes it. */
export function HelpTip({
  label,
  children,
  align = 'start',
}: {
  label: string
  children: ReactNode
  align?: 'start' | 'end'
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const id = useId()
  const root = useRef<HTMLSpanElement>(null)
  const bubble = useRef<HTMLSpanElement>(null)
  const [place, setPlace] = useState<TipPlacement | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node
      if (!root.current?.contains(target) && !bubble.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    let frame = 0
    const measure = () => {
      frame = 0
      if (!root.current || !bubble.current) return
      const r = root.current.getBoundingClientRect()
      const b = bubble.current.getBoundingClientRect()
      // clientWidth/Height leave out the page scrollbar, which would otherwise cover the bubble's edge.
      const viewport = { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight }
      const next = placeHelpTip(r, { width: b.width, height: b.height }, viewport, align)
      setPlace((prev) => (samePlacement(prev, next) ? prev : next))
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, { capture: true, passive: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, { capture: true })
    }
  }, [open, align])

  return (
    <span ref={root} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={t('action.moreAbout', { label })}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="rounded-full p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-accent dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open &&
        createPortal(
          // Portalled and fixed so no overflow-hidden card, scroll rail or static wrapper can clip or shift it.
          <span
            id={id}
            ref={bubble}
            role="note"
            style={{ left: place?.left ?? 0, top: place?.top ?? 0, visibility: place ? 'visible' : 'hidden' }}
            className="fixed z-[60] block w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-slate-200 bg-white p-3 text-left text-xs font-normal normal-case leading-snug tracking-normal text-slate-700 shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            {children}
          </span>,
          document.body,
        )}
    </span>
  )
}
